/**
 * Relative age of a maintenance ping, e.g. 'just now' / '5m ago' / '2h ago' /
 * '3d ago'. Returns null when there is no usable timestamp, so the caller can
 * OMIT the age segment rather than render 'NaN'.
 *
 * `requestedAt` is a NAIVE LOCAL-TIME ISO string ('2026-09-22T08:30:00', no
 * 'Z', no offset) — see the comment on RepairOrderPing.requestedAt. `new Date()`
 * parses that as LOCAL time, which is exactly right. Do NOT append 'Z': that
 * shifts every age by the local UTC offset and shows a one-minute-old ping as
 * "5h ago".
 *
 * This is the SINGLE relative-age helper for maintenance pings. It shipped from
 * components/RepairOrderCardList and is still re-exported from there for the
 * callers that import it by that path; it lives here so the chip component and
 * the card list can both use it without importing each other.
 */
export const formatPingAge = (requestedAt: string | null, now: Date = new Date()): string | null => {
  if (!requestedAt) return null;
  const then = new Date(requestedAt).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.floor((now.getTime() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export default formatPingAge;
